import React, {useContext, useMemo, useState} from 'react';
import {Button, Modal, Switch, Table, Tag, Typography} from 'antd';
import dayjs from 'dayjs';
import {AuthContext} from '../../../shared/contexts/authContext';
import {useShopPackage} from '../../probability/useItemData';
import {usePersonalTableData} from './usePersonalPackage';
import IsLoading from '../../../shared/components/IsLoading';
import ErrorPage from '../../../shared/components/ErrorPage';
import ModalBase from '../../../shared/components/modal/ModalBase';
import StoreManagerEditModal from '../StoreManagerEditModal';
import toast from 'react-hot-toast';
import {internalApi} from '../../../common/axios';
import ServerRefresh from "../../../shared/components/common/ServerRefresh";
import {REFRESH_TYPE_FUNCTION} from "../../../shared/utils/enum";
import {useReloadTime} from "../../serverInfo/useServerInfo";

const { Text} = Typography;

const PersonalPackageManager = () => {
    const {currentUser} = useContext(AuthContext);

    const [selectedRecord, setSelectedRecord] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [showAll, setShowAll] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState({});
    const [deleting, setDeleting] = useState(false);
    const [expandedKeys, setExpandedKeys] = useState([]);

    const todayStart = dayjs().startOf('day').unix();

    const {data: tableData, isLoading, error, refetch} = usePersonalTableData();
    const {data: shopPackage, isLoading: isShopLoading, error: shopError} = useShopPackage();
    const {data: reloadTimeData, isLoading: isReloadTimeData, error: reloadTimeDataError, refetch: reloadTimeDataRefetch} = useReloadTime();

    const handleSwitchChange = async (checked, record) => {
        try {
            if (record.end < dayjs().unix()) {
                toast.error('종료된 상점 아이템은 On/Off 상태를 변경할 수 없습니다.');
                return;
            }
            await internalApi.post('/store_manager/onoff', {seq: record.seq, onOff: checked ? 1 : 0});
            toast.success('On/Off 상태가 변경되었습니다.');
            refetch();
        } catch {
            toast.error('On/Off 변경 실패');
        }
    };

    const handleRowClick = (record) => {
        setSelectedRecord(record);
        setModalOpen(true);
    };

    const handleModalClose = () => {
        setModalOpen(false);
        setSelectedRecord(null);
        refetch();
    };

    const totalSelected = useMemo(
        () => Object.values(selectedRowKeys).reduce((sum, keys) => sum + keys.length, 0),
        [selectedRowKeys]
    );

    const handleBulkDelete = async () => {
        const seqList = Object.values(selectedRowKeys).flat();
        if (seqList.length === 0) return;
        setDeleting(true);
        try {
            await internalApi.post('/store_manager/bulk_delete', {seqList});
            toast.success(`${seqList.length}건 삭제 완료`);
            setSelectedRowKeys({});
            refetch();
        } catch {
            toast.error('삭제 실패');
        } finally {
            setDeleting(false);
        }
    };

    // 일반 상품 선택 복사와 같은 API. isPersonal(관리번호)도 그대로 복사된다.
    const handleBulkCopy = () => {
        const seqList = Object.values(selectedRowKeys).flat();
        if (seqList.length === 0) return;
        Modal.confirm({
            title: '선택 상품 복사',
            content: `선택한 ${seqList.length}건을 복사합니다. 복사 시 시작일과 종료일에 1일씩 더해지고 Off 상태로 생성됩니다.`,
            okText: '복사',
            cancelText: '취소',
            onOk: async () => {
                try {
                    await internalApi.post('/store_manager/bulk_copy', {seqList});
                    toast.success(`${seqList.length}건 복사 완료`);
                    setSelectedRowKeys({});
                    refetch();
                } catch {
                    toast.error('복사 실패');
                }
            },
        });
    };

    const groupedData = useMemo(() => {
        if (!tableData) return [];
        const groups = {};
        tableData.forEach(row => {
            const key = row.isPersonal;
            if (!groups[key]) {
                groups[key] = {
                    key: `group-${key}`,
                    isPersonal: key,
                    personalMemo: row.personalMemo,
                    items: [],
                };
            }
            groups[key].items.push(row);
        });
        return Object.values(groups).sort((a, b) => b.isPersonal - a.isPersonal);
    }, [tableData]);

    const packageColumns = [
        {title: '시퀀스', dataIndex: 'seq', key: 'seq', width: 90, sorter: (a, b) => a.seq - b.seq},
        {title: '패키지 인덱스', dataIndex: 'index', key: 'index'},
        {title: '패키지 이름', dataIndex: 'packageName', key: 'packageName'},
        {
            title: 'On/Off',
            dataIndex: 'onOff',
            render: (_, record) => (
                <Switch
                    checked={record.onOff === 1}
                    checkedChildren="On"
                    unCheckedChildren="Off"
                    onChange={checked => handleSwitchChange(checked, record)}
                    onClick={(_, e) => e.stopPropagation()}
                />
            ),
        },
        {
            title: '시작 시간', dataIndex: 'begin', key: 'begin',
            render: begin => dayjs.unix(begin).format('YYYY-MM-DD HH:mm:ss'),
        },
        {
            title: '종료 시간', dataIndex: 'end', key: 'end',
            render: end => dayjs.unix(end).format('YYYY-MM-DD HH:mm:ss'),
        },
        {title: '패키지 별 운영기간(h)', dataIndex: 'packageTime', key: 'packageTime'},
        {title: '할인 타입', dataIndex: 'saleType', key: 'saleType'},
        {title: '메모', dataIndex: 'memo', key: 'memo', width: 200},
        {
            title: '최소 적용 버전', key: 'minVersion',
            render: (_, {index}) => (
                <Tag>{shopPackage?.[currentUser.target]?.[index]?.openVersion || '-'}</Tag>
            ),
        },
    ];

    const groupColumns = [
        {
            title: '관리번호', dataIndex: 'isPersonal', key: 'isPersonal', width: 130,
            render: v => <Tag color="purple">관리번호 {v}</Tag>,
        },
        {title: '메모', dataIndex: 'personalMemo', key: 'personalMemo'},
        {
            title: '등록된 패키지 수', key: 'count', width: 140,
            render: (_, record) => `${record.items.length}개`,
        },
    ];

    const expandedRowRender = (groupRecord) => {
        const groupKey = groupRecord.isPersonal;
        const items = (showAll
            ? groupRecord.items
            : groupRecord.items.filter(item => item.end >= todayStart)
        ).map((item, i) => ({...item, key: item.seq ?? i}));

        const rowSelection = {
            type: 'checkbox',
            selectedRowKeys: selectedRowKeys[groupKey] || [],
            onChange: (keys) => setSelectedRowKeys(prev => ({...prev, [groupKey]: keys})),
        };

        return (
            <Table
                columns={packageColumns}
                dataSource={items}
                rowSelection={rowSelection}
                pagination={{pageSize: 60}}
                size="small"
                scroll={{x: 'max-content'}}
                onRow={record => ({
                    onClick: (e) => {
                        if (e.target.closest('.ant-table-selection-column')) return;
                        handleRowClick(record);
                    },
                    style: {cursor: 'pointer'},
                })}
            />
        );
    };

    if (isLoading || isShopLoading || isReloadTimeData) return <IsLoading/>;
    if (error || shopError || reloadTimeDataError) return <ErrorPage error={error || shopError}/>;

    return (
        <div>
            <div style={{marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8}}>
                <Switch
                    checked={showAll}
                    onChange={setShowAll}
                    checkedChildren="전체 보기"
                    unCheckedChildren="오늘 이후만"
                />
                <Button disabled={totalSelected === 0} onClick={handleBulkCopy}>
                    선택복사 {totalSelected > 0 ? `(${totalSelected}건)` : ''}
                </Button>
                <Button
                    danger
                    disabled={totalSelected === 0}
                    loading={deleting}
                    onClick={handleBulkDelete}
                >
                    선택삭제 {totalSelected > 0 ? `(${totalSelected}건)` : ''}
                </Button>
                <ServerRefresh refreshTypeFunc={REFRESH_TYPE_FUNCTION.GAME_DATA}
                               reloadTimeRefetch={reloadTimeDataRefetch}/>
                <Text type="secondary" style={{fontSize: '14px', marginTop: 4}}>
                    마지막 동기화 (이벤트, 상점정보 등) : {reloadTimeData.game_data_sync_id ? reloadTimeData.game_data_sync_id + ' (KST)' : '정보 없음'}
                </Text>
            </div>
            <Table
                columns={groupColumns}
                dataSource={groupedData}
                expandable={{
                    expandedRowRender,
                    expandedRowKeys: expandedKeys,
                    onExpandedRowsChange: keys => setExpandedKeys([...keys]),
                    columnTitle: (
                        <button
                            type="button"
                            aria-label={expandedKeys.length ? '모두 접기' : '모두 펼치기'}
                            title={expandedKeys.length ? '모두 접기' : '모두 펼치기'}
                            className={`ant-table-row-expand-icon ant-table-row-expand-icon-${expandedKeys.length ? 'expanded' : 'collapsed'}`}
                            onClick={() => setExpandedKeys(expandedKeys.length ? [] : groupedData.map(g => g.key))}
                        />
                    ),
                }}
                pagination={false}
                size="middle"
            />

            <ModalBase
                open={modalOpen}
                handleClose={handleModalClose}
                rowData={selectedRecord}
                ModalPage={StoreManagerEditModal}
                width={900}
                resizable
            />
        </div>
    );
};

export default PersonalPackageManager;
